/**
 * @voltxampere/web — Studio entry (Sprint 11).
 *
 * Boots the shell:
 *   1. Apply Zenit global CSS (tokens → reset → fonts → global).
 *   2. Side-effect import every Lit custom-element module so
 *      `customElements.define` runs before `<vxa-app>` is parsed.
 *   3. Hydrate the locale from `localStorage` (falls back to TR).
 *   4. Persist locale + keep `<html lang>` in sync with mutations.
 *   5. Register built-in shortcuts and attach the global
 *      keydown listener.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect } from "@preact/signals-core";

// Stylesheet ordering — tokens load first so resets / globals
// can already consume CSS variables. Fonts can load in parallel
// with the network so order between them is cosmetic.
import "./styles/tokens.css";
import "./styles/reset.css";
import "./styles/fonts.css";
import "./styles/global.css";

// Side-effect import: every component file calls `@customElement`
// at top level. The root suffices because each component imports
// its children, but we also import the modal teleport elements
// directly so HMR boundaries stay clean.
import "./components/vxa-app.js";

import { currentLocale, isLocale } from "./store/locale.js";
import { currentCircuit } from "./store/circuit.js";
import { selectedNodes } from "./store/canvas.js";
import { bindProbe } from "./store/channels.js";
import { runDc, setDcRunner } from "./store/dc-result.js";
import {
  runTransient,
  setTransientRunner,
  startLive,
  stopLive,
  transientLive,
} from "./store/transient-runner.js";
import { parseACWire, setACRunner } from "./store/ac-runner.js";
import { filterDamagedCircuit } from "./engine/circuit-filter.js";
import { bootAutoSave } from "./store/auto-save.js";
// Sprint 56c — ERC auto-run on circuit save + DC complete.
import { bootErc } from "./store/erc.js";
// Sprint 56b — Holographic formula detector auto-run.
import { bootFormulaOverlay } from "./store/formula-overlay.js";
// Sprint 58b — connection animation lifecycle + solder shimmer reset.
import { bootConnectionAnimation } from "./store/connection-animation.js";
import { bootSolderShimmer } from "./store/solder-shimmer-state.js";
import { maybeParseShareHash } from "./store/url-share.js";
import { acConfigDialogOpen } from "./components/vxa-ac-config-dialog.js";
import { listCircuits, pushRecent } from "@voltxampere/persistence";
import type { Circuit } from "@voltxampere/schema";
import { undoStack } from "./store/undo-stack.js";
import { registerBuiltinShortcuts } from "./shortcuts/catalog.js";
import { commandRegistry } from "./commands/registry.js";
import { registerBuiltinCommands } from "./commands/builtin/index.js";
import { initRecentCommandIds } from "./commands/recent.js";
import { initBookmarks } from "./store/bookmarks-persistence.js";
import { installAlwaysLiveAutoRerun } from "./store/always-live-state.js";
import { installIdleListeners } from "./store/idle-state.js";
import { initLiveModePrefs } from "./store/live-mode-prefs.js";
import { initThermalPrefs } from "./store/thermal-prefs.js";
import { initExplosionPrefs } from "./store/explosion-prefs.js";
import { initDamagePrefs } from "./store/damage-prefs.js";
import { initDamageAudioPrefs } from "./store/damage-audio-prefs.js";
import { highContrast, initA11yPrefs, reducedMotion } from "./store/a11y-state.js";
import { fontSize, initFontSizePrefs, FONT_SIZES } from "./store/font-size-state.js";
import { initPlaybackSpeed } from "./store/playback-speed.js";
// Sprint 59 — realisticMode (Aşama 2 foundation) hydrate.
import { initRealisticMode } from "./store/realistic-mode.js";
// Sprint 60 — ERC ↔ realisticMode bridge (S56c backlog closure).
import { bootErcRealisticBridge } from "./store/erc-realistic-bridge.js";
import { startDamageAudioDispatcher } from "./audio/damage-audio-dispatcher.js";
// Sprint 23 C2 — onboarding tour boot (IDB settings store hydrate +
// auto-start on first launch).
import { initTour } from "./onboarding/tour-boot.js";
import "./components/vxa-onboarding-tour.js";
// Sprint 23 D1 — custom shortcut overrides hydrate from settings store.
import { initShortcutOverrides } from "./store/shortcut-overrides.js";
import {
  getCanvasShortcutHandlers,
  registerCanvasShortcuts,
  setCanvasShortcutFallback,
} from "./shortcuts/canvas-catalog.js";
import { attachShortcutHandlers } from "./shortcuts/handlers.js";
import { canvasDemo } from "./fixtures/canvas-demo.js";
import { bench50, bench500 } from "./fixtures/bench-grid.js";
import { transientDemo } from "./fixtures/transient-demo.js";
import { setInspectorHandlers, type InspectorHandlers } from "./components/vxa-inspector.js";
import {
  BatchCommand,
  DeleteAnnotationCommand,
  EditNetLabelCommand,
  EditParamCommand,
  type Command,
  type EditableParamValue,
} from "@voltxampere/ui/canvas";
import { solveAc, solveDc, solveTransient } from "./store/engine-worker-client.js";

const LOCALE_STORAGE_KEY = "vxa.locale";

function hydrateLocale(): void {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) {
      currentLocale.value = stored;
    }
  } catch {
    // localStorage may throw on Safari private mode — ignore.
  }
}

function bootLocalePersistence(): void {
  effect(() => {
    const locale = currentLocale.value;
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // ignore storage failures
    }
  });
}

/**
 * Sprint 18 Paket C C4 — boot pre-selection.
 *
 * The mockup (zenit-platform.html) ships with R₁ pre-selected
 * so the inspector immediately reads a real value. Pick the
 * canonical R₁ when present; otherwise fall back to the first
 * passive component (resistor / capacitor / inductor / diode /
 * BJT / MOSFET) so sources and ground stay deselected.
 *
 * Returns null when nothing matches — selection stays empty
 * which renders the empty-state inspector branch as before.
 */
export function pickInitialSelection(circuit: typeof canvasDemo): string | null {
  const components = circuit.components;
  if (components.length === 0) return null;
  const byLabel = components.find((c) => c.label === "R1" || c.label === "R₁");
  if (byLabel) return byLabel.id;
  const passive = components.find(
    (c) =>
      c.type !== "voltage-source" &&
      c.type !== "dc-source" &&
      c.type !== "current-source" &&
      c.type !== "ground",
  );
  return passive?.id ?? null;
}

/**
 * Sprint 19 F2 — boot probe binding.
 *
 * The mockup's measurement node is `n2` (the R₁ / R₂ junction).
 * On boot we attach CH1 to that net so the user opens the app
 * and the inspector's measurement tile + the waveform trace
 * are populated immediately. Without this hook the user sees
 * empty grids (Sprint 18 functional gap diagnosed in
 * `DIAGNOSTIC_FUNCTIONAL.md`).
 *
 * Selection rule:
 *   1. Prefer the net named exactly "mid" — every voltage-
 *      divider-style fixture (boot, presets, future tutorials)
 *      uses that name for the canonical measurement node.
 *   2. Otherwise return the first non-ground net.
 *   3. Otherwise return null (an all-ground circuit has nothing
 *      meaningful to probe).
 *
 * Returning null is safe — `bindProbe` will simply not be
 * called and CH1 stays unbound (the empty-state CTA branch
 * shipped in F3 then kicks in).
 */
export function pickInitialProbeBinding(circuit: typeof canvasDemo): string | null {
  const nets = circuit.nets;
  const byName = nets.find((n) => n.name === "mid" && !n.isGround);
  if (byName) return byName.id;
  const firstSignal = nets.find((n) => !n.isGround);
  return firstSignal?.id ?? null;
}

function pickInitialCircuit(): typeof canvasDemo {
  // Sprint 12: development convenience — `?fixture=bench-50` /
  // `?fixture=bench-500` swap the canvas demo for the perf grids
  // so Şefim can confirm 60 fps in DevTools without rebuilding.
  // Production builds always start on `canvasDemo`.
  if (typeof window === "undefined") return canvasDemo;
  try {
    const params = new URLSearchParams(window.location.search);
    const fx = params.get("fixture");
    if (fx === "bench-50") return bench50;
    if (fx === "bench-500") return bench500;
    if (fx === "transient-demo") return transientDemo;
  } catch {
    // ignore — fall through to default
  }
  return canvasDemo;
}

/**
 * Sprint 14 → 14.0.1 (D6 closure) — every inspector edit goes
 * through the shared `undoStack.execute(cmd, circuit)` so a
 * single Cmd+Z reverts whether the action came from the canvas
 * tools or the inspector. Tests inject their own handlers via
 * `setInspectorHandlers(...)` and bypass the singleton.
 */
function wireInspectorHandlers(): void {
  const editParamHandler: InspectorHandlers["editParam"] = (
    componentIds,
    fieldKey,
    newValue,
  ): void => {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const commands: Command[] = [];
    for (const id of componentIds) {
      const cmp = circuit.components.find((c) => c.id === id);
      if (!cmp) continue;
      const oldValue = cmp.params[fieldKey] as EditableParamValue | undefined;
      commands.push(new EditParamCommand(id, fieldKey, oldValue, newValue as EditableParamValue));
    }
    if (commands.length === 0) return;
    const cmd =
      commands.length === 1 && commands[0]
        ? commands[0]
        : new BatchCommand(commands, `${String(commands.length)} parametre güncellendi`);
    currentCircuit.value = undoStack.execute(cmd, circuit);
  };

  const editNetLabelHandler: InspectorHandlers["editNetLabel"] = (
    netId,
    oldName,
    newName,
  ): void => {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    currentCircuit.value = undoStack.execute(
      new EditNetLabelCommand(netId, oldName, newName),
      circuit,
    );
  };

  const deleteAnnotationHandler: InspectorHandlers["deleteAnnotation"] = (id): void => {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    currentCircuit.value = undoStack.execute(new DeleteAnnotationCommand(id, circuit), circuit);
  };

  const runDcHandler: InspectorHandlers["runDc"] = (): void => {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    void runDc(circuit);
  };

  // Sprint 18 Paket C C3 — Rotate / Copy / Delete buttons in the
  // inspector route through the canvas's existing shortcut-handler
  // table. Looking the table up at click time (instead of caching
  // it during boot) means the inspector keeps working even when
  // the canvas remounts mid-session, and the dispatch follows the
  // identical path Cmd+C / Delete already exercise.
  const dispatchCanvas = (slot: "copy" | "deleteSelected" | "rotateSelected"): void => {
    const handlers = getCanvasShortcutHandlers();
    if (!handlers) return;
    handlers[slot]();
  };
  const rotateSelectionHandler: InspectorHandlers["rotateSelection"] = (): void => {
    dispatchCanvas("rotateSelected");
  };
  const copySelectionHandler: InspectorHandlers["copySelection"] = (): void => {
    dispatchCanvas("copy");
  };
  const deleteSelectionHandler: InspectorHandlers["deleteSelection"] = (): void => {
    dispatchCanvas("deleteSelected");
  };

  setInspectorHandlers({
    editParam: editParamHandler,
    editNetLabel: editNetLabelHandler,
    deleteAnnotation: deleteAnnotationHandler,
    runDc: runDcHandler,
    rotateSelection: rotateSelectionHandler,
    copySelection: copySelectionHandler,
    deleteSelection: deleteSelectionHandler,
  });
}

/**
 * Sprint 14.0.1 (D7 closure) — wire the real engine worker.
 * `solveDc` lazily spins a Vite-bundled Web Worker on first
 * call, runs `init` then `runAnalysis({ kind: "DC" })`, and
 * adapts the engine's `AnalysisResult` shape to the
 * `DCResultLike` the store + inspector consume.
 */
function bootDcRunner(): void {
  if (typeof Worker === "undefined") {
    console.warn("[VXA] Web Worker unavailable in this runtime; DC runner disabled");
    return;
  }
  setDcRunner(async (circuit) => {
    const result = await solveDc(circuit);
    return {
      converged: result.converged,
      iterations: result.iterations,
      nodeVoltages: result.nodeVoltages,
      branchCurrents: result.branchCurrents,
      warnings: result.warnings,
    };
  });
}

/**
 * Sprint 15 — bind the transient runner to the engine worker. The
 * wire format's `meta.timeSeries` (ADR-0027) carries the per-step
 * samples; we project them into the `TransientResultLike` shape
 * the waveform store consumes.
 */
function bootTransientRunner(): void {
  if (typeof Worker === "undefined") {
    console.warn("[VXA] Web Worker unavailable in this runtime; transient runner disabled");
    return;
  }
  setTransientRunner(async (circuit, opts) => {
    // Sprint 36-4 — drop / substitute permanently-damaged components
    // before they reach the engine. Engine zero-diff invariant guard.
    const filtered = filterDamagedCircuit(circuit);
    if (
      import.meta.env.DEV &&
      (filtered.removedIds.length > 0 || filtered.substitutedIds.length > 0)
    ) {
      console.info(
        `[VXA F5] damage filter · removed=[${filtered.removedIds.join(",")}] substituted=[${filtered.substitutedIds.join(",")}]`,
      );
    }
    const result = await solveTransient(filtered.circuit, {
      ...(opts?.tstop !== undefined ? { tstop: opts.tstop } : {}),
      ...(opts?.signal ? { signal: opts.signal } : {}),
    });
    const meta = result.meta as {
      timeSeries?: readonly {
        t: number;
        nodeVoltages: Record<string, number>;
        sourceCurrents: Record<string, number>;
      }[];
      samples?: number;
    };
    return {
      converged: result.converged,
      samples: meta.samples ?? meta.timeSeries?.length ?? 0,
      timeSeries: meta.timeSeries ?? [],
      warnings: result.warnings,
    };
  });
}

/**
 * Sprint 20 — AC runner boot. Same Worker-or-disabled lifecycle as
 * DC / transient. The wire envelope's `meta.acFrequencies` +
 * `meta.acNode*` arrays are bundled by `parseACWire` into the
 * store's per-net / per-branch trace maps.
 */
function bootACRunner(): void {
  if (typeof Worker === "undefined") {
    console.warn("[VXA] Web Worker unavailable in this runtime; AC runner disabled");
    return;
  }
  setACRunner(async (circuit, opts) => {
    // Sprint 36-4 — same damage filter as the transient runner.
    const filtered = filterDamagedCircuit(circuit);
    if (
      import.meta.env.DEV &&
      (filtered.removedIds.length > 0 || filtered.substitutedIds.length > 0)
    ) {
      console.info(
        `[VXA F6] damage filter · removed=[${filtered.removedIds.join(",")}] substituted=[${filtered.substitutedIds.join(",")}]`,
      );
    }
    const result = await solveAc(filtered.circuit, {
      ...(opts?.sweep !== undefined ? { sweep: opts.sweep } : {}),
      ...(opts?.signal ? { signal: opts.signal } : {}),
    });
    return parseACWire({
      converged: result.converged,
      warnings: result.warnings,
      meta: result.meta,
    });
  });
}

hydrateLocale();
bootLocalePersistence();
registerBuiltinShortcuts();
registerCanvasShortcuts();
attachShortcutHandlers(window);

// Sprint 22 C3 — populate the Cmd+K palette with the canonical
// 100+ command set. Idempotent only on a fresh registry; the boot
// path runs exactly once per page load, so a duplicate-id throw
// here would signal an HMR mistake worth surfacing loudly.
registerBuiltinCommands(commandRegistry);

// Sprint 22 C4 — hydrate the Recent section from the IDB
// commandRecents store (DB v2). Fire-and-forget; the palette
// works fine before hydration completes (Recent simply renders
// empty until the read resolves).
void initRecentCommandIds().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} recent command(s) from IDB`);
  }
});

// Sprint 32-4 — bookmarks (per-circuit time scrubber pins) IDB
// hydrate. Same fire-and-forget pattern as the recent commands
// hydrate above. Boot path is non-blocking; the scrubber renders an
// empty pin set until the read resolves (typically <10ms on warm
// connections).
void initBookmarks().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} bookmark(s) from IDB`);
  }
});

// Sprint 33-1 — always-live auto-rerun watcher. Subscribes to
// currentCircuit + alwaysLiveEnabledSignal; debounced 250ms re-run
// on change. Default ON; AYARLAR > Sürekli Akış (33-4) toggles +
// IDB persistence (33-4) follow.
installAlwaysLiveAutoRerun();

// Sprint 33-2 — idle detection global listeners (mousedown /
// mousemove / keydown / touchstart / wheel). 100ms record throttle,
// 3-minute idle threshold. RAF throttle (also 33-2) reads
// `isIdleSignal` to gate fps 60 → 10. Default ON; AYARLAR toggle
// in 33-4 + IDB persist (33-4) follow.
installIdleListeners();

// Sprint 33-4 — live-mode prefs hydrate from IDB settings store
// (always-live + idle detection toggles). Fire-and-forget; signals
// keep their default-true values until the read resolves.
void initLiveModePrefs().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} live-mode pref(s) from IDB`);
  }
});

// Sprint 34-5 — thermal pref hydrate (canvas heatmap overlay
// toggle). Same fire-and-forget pattern; default-true until read
// resolves.
void initThermalPrefs().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} thermal pref(s) from IDB`);
  }
});

// Sprint 35-5 — explosion + smoke prefs hydrate. Master toggle +
// separate smoke toggle (perf-conscious). Sprint 36 canvas overlay
// effect will read these signals to gate fx layer.
void initExplosionPrefs().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} explosion pref(s) from IDB`);
  }
});

// Sprint 36-5 — damage prefs hydration (persistAcrossReload +
// wireOverloadEnabled toggles + damage snapshot restore). The
// snapshot restore lands BEFORE the canvas mounts so previously-
// damaged components show up as such on first paint.
void initDamagePrefs().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} damage pref(s) / snapshot from IDB`);
  }
});

// Sprint 37-4 — damage audio prefs hydration (8 sound effects
// master toggle). Hydrate FIRST, then start the dispatcher so the
// effect installation reads the persisted enabled flag.
void initDamageAudioPrefs().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} damage-audio pref(s) from IDB`);
  }
  // Sprint 37-2 — install dispatcher after pref hydration so the
  // initial enabled flag is correct. Disposer captured here is not
  // wired to a teardown — main.ts boot is page-lifetime.
  startDamageAudioDispatcher();
});

// Sprint 53 — playback speed hydrate (toolbar speed dropdown
// default; VISION §6.E, vision-audit Item 19). Restores the user's
// last picked default from IDB; fall back to 1× (DEFAULT_PLAYBACK_SPEED)
// until the read resolves.
void initPlaybackSpeed().then((speed) => {
  if (speed !== 1 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated playback default speed = ${String(speed)}×`);
  }
});

// Sprint 80.Z B1 — a11y high-contrast pref hydrate (Sprint 24
// backlog "S25-A11Y-HIGH-CONTRAST-UI-BINDING" closure). The signal
// drives a `body.high-contrast` class via the effect below so the
// global stylesheet's overrides cascade automatically.
void initA11yPrefs();
effect(() => {
  const enabled = highContrast.value;
  document.body.classList.toggle("high-contrast", enabled);
});

// Sprint 80.Z L-AYARLAR-2 — reduced-motion pref. Drives
// `body.reduce-motion` so the global stylesheet's `* { animation-
// duration: 0.001ms; transition-duration: 0.001ms; }` override
// kicks in. Hydration in initA11yPrefs() honours the OS
// `prefers-reduced-motion` media query when no explicit user
// preference is persisted.
effect(() => {
  document.body.classList.toggle("reduce-motion", reducedMotion.value);
});

// Sprint 80.Z L-AYARLAR-1 — font size pref hydrate. Drives
// `body.font-{S,L,XL}` classes (M = default, no class). CSS
// overrides in global.css apply curated rem scaling per class.
void initFontSizePrefs();
effect(() => {
  const size = fontSize.value;
  for (const s of FONT_SIZES) {
    document.body.classList.toggle(`font-${s}`, s === size && s !== "M");
  }
});

// Sprint 59 — realisticMode (Aşama 2 foundation) hydrate. Default off
// so existing users see no behavioral change until they opt in via
// Settings → "Gerçekçi mod" or the topbar indicator badge.
void initRealisticMode();
// Sprint 60 — bridge realisticMode signal → engine overheating rule
// config so the user toggle is end-to-end functional (S56c backlog
// closure pre-launch).
bootErcRealisticBridge();

// Sprint 23 C2 — onboarding tour: read settings store, auto-start
// on first launch (idempotent — subsequent boots skip when shown).
void initTour();

// Sprint 23 D1 — hydrate custom shortcut overrides from settings
// store. Fire-and-forget; ShortcutManager continues with defaults
// until the read resolves.
void initShortcutOverrides().then((count) => {
  if (count > 0 && import.meta.env.DEV) {
    console.info(`[VXA boot] hydrated ${String(count)} shortcut override(s) from IDB`);
  }
});

// Sprint 23 F1 — hydrate canvas + theme preferences (canvasStyle,
// wireCurve, symbolStandard) from settings store.
void import("./store/canvas-prefs.js").then(({ initCanvasPrefs }) => initCanvasPrefs());

// Sprint 24 A3 — hydrate AC/transient/temperature defaults from
// settings store. Empty IDB row keeps factory defaults intact.
void import("./store/sim-prefs.js").then(({ initSimPrefs }) => initSimPrefs());

// Sprint 26 Lane 10 — hydrate inspector collapsible-section open
// state from settings store. Empty IDB row keeps Sprint 25 R5-IA-3
// static defaults (FORMÜL/CANLI ÖLÇÜMLER/DC all closed).
void import("./store/inspector-prefs.js").then(({ initInspectorPrefs }) => initInspectorPrefs());

// Sprint 29-5 — bind the OS `prefers-reduced-motion` media query so
// the @voltxampere/fx layer can throttle / skip particle animation
// when the user has accessibility preferences set. Idempotent; the
// signal value flips live when the OS preference changes mid-session.
void import("./store/fx-state.js").then(({ bindReducedMotionMedia }) => {
  bindReducedMotionMedia();
});

// Sprint 31-6 — bind document.visibilityState listener so audio
// silences automatically when the user switches tabs / minimises
// the window. Prevents background tabs from beeping unexpectedly.
void import("./store/audio-state.js").then(({ bindVisibilityListener, ensureAudioEngine }) => {
  bindVisibilityListener();
  // Audio engine itself is NOT started here — the browser's
  // autoplay policy requires a user gesture. The first Run/F5/
  // AYARLAR slider drag triggers ensureAudioEngine() lazily. We
  // import it eagerly so the dynamic-import promise resolves before
  // the user clicks anything (snappier first-gesture latency).
  void ensureAudioEngine;
});

// Sprint 31-6 — wire the audio engine update loop to the
// currentAudioFrame signal. When the frame's component map changes
// (transient finished, V_pin crossed threshold, AYARLAR toggle, etc),
// the Tone.js engine reconciles its oscillator pool.
void import("./store/audio-state.js").then(
  ({ audioMasterVolume, audioMuted, currentAudioFrame, documentHidden, getAudioEngine }) => {
    void import("@preact/signals-core").then(({ effect }) => {
      void import("./store/fx-state.js").then(({ prefersReducedMotion }) => {
        effect(() => {
          const frame = currentAudioFrame.value;
          const engine = getAudioEngine();
          if (!engine) return;
          engine.update(frame, {
            masterVolume: audioMasterVolume.value,
            muted: audioMuted.value,
            suppressed: prefersReducedMotion.value || documentHidden.value,
          });
        });
      });
    });
  },
);

/**
 * Sprint 21 C6 — Boot circuit picker priority chain:
 *
 *   1. URL hash share (#share=base64url) — explicit user intent
 *   2. IndexedDB last-saved circuit — keeps the editor warm
 *   3. Fixture fallback — fresh-install default
 *
 * Each step is async; we resolve the chain before flipping
 * `currentCircuit.value` so the boot trace logs the resolved
 * source. Failures at any step fall through to the next; main.ts
 * never crashes on persistence I/O.
 */
async function pickBootCircuit(): Promise<Circuit> {
  // 1. URL hash share.
  if (typeof window !== "undefined") {
    try {
      const fromHash = await maybeParseShareHash(window.location.hash);
      if (fromHash) {
        if (import.meta.env.DEV) {
          console.info("[VXA boot] source=share-hash circuit=" + fromHash.name);
        }
        return fromHash;
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn("[VXA boot] share-hash decode failed:", err);
      }
    }
  }
  // 2. IndexedDB last-saved.
  try {
    const stored = await listCircuits({ direction: "desc", limit: 1 });
    const last = stored[0];
    if (last) {
      if (import.meta.env.DEV) {
        console.info("[VXA boot] source=indexeddb circuit=" + last.circuit.name);
      }
      return last.circuit;
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA boot] indexeddb load failed:", err);
    }
  }
  // 3. Fixture fallback (synchronous).
  const fixture = pickInitialCircuit();
  if (import.meta.env.DEV) {
    console.info("[VXA boot] source=fixture circuit=" + fixture.name);
  }
  return fixture;
}

const _bootCircuitRaw = await pickBootCircuit();
// Sprint 24 A1 (B1 R2 drift fix) — structuredClone the boot
// circuit, regardless of source (URL hash, IDB, fixture). Sprint
// 19 ADR-0036'da boot fixture canvas-demo module-level constant —
// preset constant referansları paylaşılmaması için defensive deep
// clone. Auto-save downstream zaten clone'lar (auto-save.ts), bu
// boot-side koruyucu reference paylaşımının başlamasını engeller.
const _bootCircuit = structuredClone(_bootCircuitRaw);
currentCircuit.value = _bootCircuit;
// Mark the boot circuit as recently opened so the AYARLAR Veri tab
// surfaces it on first paint. Failures are non-fatal — the editor
// still works without the recents bookkeeping.
void pushRecent(_bootCircuit.id, _bootCircuit.name).catch(() => undefined);
{
  const initialId = pickInitialSelection(currentCircuit.value);
  if (initialId !== null) selectedNodes.value = [initialId];
  const initialProbeNet = pickInitialProbeBinding(currentCircuit.value);
  if (initialProbeNet !== null) bindProbe("ch1", initialProbeNet);
  // Sprint 19 hotfix-3 — DEV-mode trace so Şefim's browser console
  // surfaces the boot pipeline state. Production builds suppress
  // this via Vite's `import.meta.env.DEV` dead-code elimination.
  if (import.meta.env.DEV) {
    const cmps = currentCircuit.value.components.length;
    const wires = currentCircuit.value.wires.length;
    console.info(
      `[VXA boot] circuit=${currentCircuit.value.name} components=${String(cmps)} wires=${String(wires)} ` +
        `selection=${initialId ?? "null"} probe.ch1=${initialProbeNet ?? "null"}`,
    );
  }
}
wireInspectorHandlers();
bootDcRunner();
bootTransientRunner();
bootACRunner();
// Sprint 21 C6 — auto-save 5 s debounce after boot.
bootAutoSave();
// Sprint 56c — ERC auto-run hook. Watches currentCircuit +
// currentDCResult; topology rules fire on save, DC-aware rules fire
// after DC settles. 300 ms debounce so live editing is smooth.
bootErc();
// Sprint 56b — holographic formula detector. Watches currentCircuit
// + selectedNodes; 200 ms debounce; surfaces matched topology
// pattern in the canvas overlay.
bootFormulaOverlay();
// Sprint 58b — connection animation flash detector + solder shimmer
// per-circuit reset. Both observe currentCircuit only; no per-frame
// cost outside the canvas fx loop.
bootConnectionAnimation();
bootSolderShimmer();

// Sprint 19 hotfix-4 — pane-independent shortcut fallbacks.
//
// F5 / Shift+F5 originally lived on `<vxa-canvas>` because Sprint
// 15 only had one pane. Sprint 18 Paket B introduced the multi-
// pane router which re-mounts `<vxa-canvas>` on hash transitions.
// Between mounts the `handlers` slot in `canvas-catalog` is null,
// so a keypress arriving in that window resolves to a no-op —
// exactly Şefim's "F5 sinyal üretmiyor" report (the boot trace
// never emitted "[VXA F5] runTransient start" because dispatch
// short-circuited on the null handler).
//
// The fallback reaches the engine pipeline directly: read the
// current circuit, call runTransient — same dispatch path the
// canvas handler uses, just routed through main's lexical scope
// so no canvas mount is required. When `<vxa-canvas>` IS mounted
// its handler wins (it knows about active selection, paste
// targets, etc.); the fallback is the safety net.
setCanvasShortcutFallback({
  runTransient: () => {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    void runTransient(circuit);
  },
  toggleLive: () => {
    if (transientLive.value) {
      stopLive();
    } else {
      startLive(() => currentCircuit.value);
    }
  },
  runAC: () => {
    // Sprint 21 C7 — F6 opens the config dialog. The dialog seeds
    // itself from the circuit's current settings and runs AC on
    // confirm. Tests bypass the dialog by calling runAC(circuit)
    // directly through the ac-runner store.
    if (currentCircuit.value === null) return;
    acConfigDialogOpen.value = true;
  },
});
